import { ListLoader } from './list-loader';
import { PaggerData } from './pager';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TableSortModel } from '../../tablesort/tableSortModel';
import { LoadingState } from './loading-state';

export abstract class AbstractListLoader<T> implements ListLoader<T> {
  protected readonly MAX_ITEMS_PER_PAGE = 1000000;

  protected currentPage: number;
  protected subscriptions = new Array<Subscription>();
  protected tableSortModel: TableSortModel<T>;
  protected _pageSize: number;
  protected _error: any;
  protected throwError = true;
  protected _totalItemsVisible: number;
  protected _totalItemsFound: number;

  protected stateSubject = new BehaviorSubject<LoadingState>(LoadingState.INIT);
  state$ = this.stateSubject.asObservable();

  protected itemsSubject = new BehaviorSubject<Array<T>>(undefined);
  items$ = this.itemsSubject.asObservable();

  protected constructor(pageSize: number) {
    if (!pageSize) {
      this._pageSize = this.MAX_ITEMS_PER_PAGE;
    } else {
      this._pageSize = pageSize;
    }
  }

  get pageSize(): string {
    return '' + this._pageSize;
  }

  set pageSize(value: string) {
    const pageSize = parseInt(value, 10);
    if (this._pageSize === pageSize) {
      return;
    }
    this._pageSize = pageSize;
    this.currentPage = 1;
    this.internalChangePage(this.currentPage);
  }

  get state(): LoadingState {
    return this.stateSubject.value;
  }

  get items(): Array<T> {
    return this.itemsSubject.value;
  }

  get pagerData(): PaggerData {
    const totalPages = Math.ceil(this.totalItemsVisible / this._pageSize);
    let startPage: number, endPage: number;
    startPage = 1;
    endPage = totalPages;

    const startIndex: number = (this.currentPage - 1) * this._pageSize;
    const endIndex: number = startIndex + this._pageSize;
    return {
      totalItems: this.totalItemsVisible,
      currentPage: this.currentPage,
      pageSize: this._pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex
    };
  }

  get resumable(): boolean {
    return false;
  }

  get stoppable(): boolean {
    return false;
  }

  get availablePageSizes(): Array<string> {
    const res = new Array<string>();
    if (!this.totalItemsVisible) {
      return res;
    }
    res.push('15');
    if (this.totalItemsVisible >= 15) {
      res.push('30');
    }
    if (this.totalItemsVisible >= 30) {
      res.push('50');
    }
    if (this.totalItemsVisible >= 50) {
      res.push('100');
    }
    return res;
  }

  get isInitialLoading(): boolean {
    return this.stateSubject.value === LoadingState.INITIAL_LOADING;
  }

  get isPageLoading(): boolean {
    return this.stateSubject.value === LoadingState.PAGE_LOADING;
  }

  get isInitialized(): boolean {
    return this.stateSubject.value !== LoadingState.INIT && this.stateSubject.value !== LoadingState.INITIAL_LOADING;
  }

  get isInitializedItemsFound(): boolean {
    return this.isInitialized && this.hasLoadedItems;
  }

  get isInitializedNoItemsFound(): boolean {
    return this.isInitialized && this.hasNoItems;
  }

  get isSuspended(): boolean {
    return false;
  }

  get allDataLoaded(): boolean {
    return this.stateSubject.value === LoadingState.ALL_DATA_LOADED;
  }

  get allDataLoadedItemsFound(): boolean {
    return this.allDataLoaded && this.hasLoadedItems;
  }

  get allDataLoadedNoItemsFound(): boolean {
    return this.allDataLoaded && this.hasNoItems;
  }

  get hasLoadedItems(): boolean {
    if (!this.itemsSubject.value) {
      return false;
    }
    return this.itemsSubject.value.length > 0;
  }

  get hasNoItems(): boolean {
    return !this.hasLoadedItems;
  }

  get totalItemsVisible(): number {
    return this._totalItemsVisible;
  }

  set totalItemsVisible(totalItems: number) {
    this._totalItemsVisible = totalItems;
  }

  get totalItemsFound(): number {
    return this._totalItemsFound;
  }

  set totalItemsFound(totalItems: number) {
    this._totalItemsFound = totalItems;
  }

  resume(): void {
    throw new Error('Loader is not resumable.');
  }

  suspend(): void {
    throw new Error('Loader is not stoppable.');
  }

  remove(item: T): void {
    throw new Error('Loader cannot remove.');
  }

  unsubscribeAll() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  changePage(page: number) {
    if (this.currentPage === page) {
      return;
    }
    this.internalChangePage(page);
  }

  protected filterAccessDenied(error: any) {
    this._error = error;
    if (error && error.status === 403) {
      this.stateSubject.next(LoadingState.ACCESS_DENIED);
      return;
    }
    this.stateSubject.next(LoadingState.LOADING_ERROR);
    if (this.throwError) {
      throw error;
    }
  }

  get error(): any {
    return this._error;
  }

  abstract applySortModel(talbeSortModel: TableSortModel<T>): void;

  protected abstract internalChangePage(page: number);
}
